import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDatePeriod } from '@hero/core';
import type { Billing, BillingItem, Currency } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { Price } from '../../../app/Price';
import { BillingStatusBadge } from '../BillingStatusBadge';
import { useBillingItemLabel } from '../BillingItemLabel';

export interface BillingItemsCardProps {
  billing: Billing;
  className?: string;
}

/**
 * Identity + line-items card for `BillingDetailPage`.
 *
 * Owns the canonical billing header (title + period + status pill)
 * so that information lives in exactly one place on the screen, and
 * lays out the line items below as pill-style rows matching the
 * checkout / overview card language.
 *
 * The page used to render the title strip separately above this card;
 * that duplicated the title (once in the TopBar, once in the page
 * header, plus this card) and left awkward whitespace next to the
 * sticky pay summary on the right.
 */
export function BillingItemsCard({ billing, className }: BillingItemsCardProps) {
  const { t } = useTranslation('m-billing');
  const currency: Currency = billing.amount.currency;
  const periodLabel = formatDatePeriod(billing.period);

  return (
    <section
      className={cn(
        'flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm',
        className,
      )}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            {billing.title}
          </h2>
          {periodLabel && (
            <p className="text-sm text-gray-500">{periodLabel}</p>
          )}
        </div>
        <BillingStatusBadge status={billing.status} />
      </header>

      <div className="flex flex-col gap-3 border-t border-gray-100 pt-5">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('billing_doc.detail.items_title')}
          </h3>
          <p className="text-xs text-gray-500">
            {t('billing_doc.detail.items_description')}
          </p>
        </div>

        <ul className="flex flex-col gap-2">
          {billing.items.map((item, idx) => (
            <ItemRow key={idx} item={item} currency={currency} />
          ))}
        </ul>
      </div>
    </section>
  );
}

interface ItemRowProps {
  item: BillingItem;
  currency: Currency;
}

function ItemRow({ item, currency }: ItemRowProps) {
  const { t } = useTranslation('m-billing');
  const label = useBillingItemLabel();
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span
          className="truncate text-sm font-medium text-gray-900"
          title={item.description}>
          {label(item)}
        </span>
        <span className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
          <span>
            {t(`billing_doc.item.${item.type}`, { defaultValue: item.type })}
          </span>
          <span aria-hidden>·</span>
          <span className="tabular-nums">{item.quantity}</span>
          <span aria-hidden>×</span>
          <Price amount={item.unitPrice} currency={currency} size="xs" />
        </span>
      </div>
      <span className="text-sm font-semibold tabular-nums text-gray-900">
        <Price amount={item.amount} currency={currency} />
      </span>
    </li>
  );
}
